/**
 * AppDialog — système de dialogs brandés cross-platform.
 *
 * Remplace window.alert / window.confirm sur web par des popups stylés
 * (cohérent avec l'UI MyTenue, intégré à AppModal). Sur native, fallback
 * sur Alert.alert classique.
 *
 * Usage :
 *   import { showAppAlert } from '../components/AppDialog';
 *
 *   // Simple message
 *   await showAppAlert('Titre', 'Message')
 *
 *   // Avec boutons custom
 *   await showAppAlert('Confirmation', 'Continuer ?', [
 *     { text: 'Annuler', style: 'cancel' },
 *     { text: 'OK', style: 'default', onPress: () => doSomething() },
 *   ])
 *
 *   // Renvoie l'index du bouton cliqué (0, 1, ...) ou -1 si fermé sans choix
 *
 * v1.12.118 — Phase 2 : popups web brandés au lieu d'alert/confirm browser.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Platform, Alert, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import AppModal from './AppModal';

// ─── Imperative API (style Alert.alert) ──────────────────────
let pushDialog = null;

/**
 * Affiche un dialog brandé. Returns Promise<number> (index du bouton cliqué).
 * @param {string} title
 * @param {string} message
 * @param {Array} buttons - [{ text, style: 'default'|'cancel'|'destructive', onPress }]
 */
export function showAppAlert(title, message, buttons) {
  if (Platform.OS !== 'web') {
    // Native : fallback Alert.alert
    return new Promise((resolve) => {
      const cocoa = (buttons && buttons.length > 0)
        ? buttons.map((b, i) => ({
            text: b.text,
            style: b.style || 'default',
            onPress: () => { try { b.onPress?.(); } catch {} resolve(i); },
          }))
        : [{ text: 'OK', onPress: () => resolve(0) }];
      Alert.alert(title || '', message || '', cocoa);
    });
  }

  // Web : utilise le host monté dans App.js
  return new Promise((resolve) => {
    if (!pushDialog) {
      // Host pas encore monté → fallback window.alert (rare au tout 1er render)
      console.warn('[AppDialog] host not mounted, fallback window.alert');
      window.alert(`${title}\n\n${message}`);
      resolve(0);
      return;
    }
    pushDialog({
      title:    title || '',
      message:  message || '',
      buttons:  buttons && buttons.length > 0 ? buttons : [{ text: 'OK', style: 'default' }],
      onResolve: (idx, btn) => {
        try { btn?.onPress?.(); } catch {}
        resolve(idx);
      },
    });
  });
}

// ─── Host (mounted in App.js root) ───────────────────────────
export function AppDialogHost() {
  const [dialog, setDialog] = useState(null); // { title, message, buttons, onResolve }
  // v1.12.639 — Ref pour le dialog courant : permet à close() d'être stable
  // (useCallback []) sans avoir dialog dans les deps. Sinon close ref change
  // à chaque setDialog → onRequestClose change → AppModal cleanup → history.back()
  // → si multiple re-renders → multiple back → traverse les 3 buffers Stripe
  // → atteint l'URL Stripe ("ok ferme popup mais ramène à Stripe checkout").
  const dialogRef = React.useRef(dialog);
  React.useEffect(() => { dialogRef.current = dialog; }, [dialog]);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    pushDialog = (d) => setDialog(d);
    return () => { pushDialog = null; };
  }, []);

  const close = useCallback((idx, btn) => {
    // v1.12.641 — ORDRE CRITIQUE: appeler onPress AVANT setDialog(null).
    // Sinon setDialog(null) unmount AppModal → useEffect cleanup → history.back()
    // s'exécute AVANT que btn.onPress puisse faire window.location.replace('/')
    // → l'user recule vers Stripe au lieu d'aller sur '/'.
    // Maintenant: onPress fire d'abord. Si onPress fait location.replace,
    // la page navigate immédiatement et setDialog(null) ne s'exécute jamais.
    const cb = dialogRef.current?.onResolve;
    try { cb?.(idx, btn); } catch {}
    setDialog(null);
  }, []);
  // v1.12.639 — onRequestClose stable (sinon AppModal cleanup multiple → trop de back)
  const handleRequestClose = useCallback(() => close(-1), [close]);

  if (Platform.OS !== 'web' || !dialog) return null;

  const { title, message, buttons } = dialog;

  return (
    <AppModal
      visible
      transparent
      animationType="fade"
      onRequestClose={handleRequestClose}
      webMaxWidth={420}
      webScrollable={false}
    >
      <View style={s.card}>
        {!!title && <Text style={s.title}>{title}</Text>}
        {!!message && <Text style={s.message}>{message}</Text>}
        <View style={[s.buttonsRow, buttons.length > 2 && s.buttonsCol]}>
          {buttons.map((b, i) => {
            const isCancel = b.style === 'cancel';
            const isDest   = b.style === 'destructive';
            return (
              <TouchableOpacity
                key={i}
                onPress={() => close(i, b)}
                activeOpacity={0.85}
                style={[
                  s.btn,
                  isCancel && s.btnCancel,
                  isDest   && s.btnDestructive,
                  !isCancel && !isDest && s.btnPrimary,
                  buttons.length > 2 && s.btnFullWidth,
                ]}
              >
                <Text style={[
                  s.btnText,
                  isCancel && s.btnCancelText,
                  isDest   && s.btnDestructiveText,
                  !isCancel && !isDest && s.btnPrimaryText,
                ]}>{b.text}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingTop: 28,
    paddingBottom: 16,
    paddingHorizontal: 24,
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1A1814',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.2,
  },
  message: {
    fontSize: 14,
    color: '#5F584F',
    lineHeight: 21,
    textAlign: 'center',
    marginBottom: 24,
    whiteSpace: 'pre-line',
  },
  buttonsRow: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center',
  },
  buttonsCol: {
    flexDirection: 'column-reverse',
    gap: 8,
  },
  btn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  btnFullWidth: {
    flex: 0,
    width: '100%',
  },
  btnPrimary: {
    backgroundColor: '#9B7B5A',
  },
  btnCancel: {
    backgroundColor: '#F5EFE8',
    borderWidth: 1,
    borderColor: '#E8E0D4',
  },
  btnDestructive: {
    backgroundColor: '#C62828',
  },
  btnText: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  btnPrimaryText:     { color: '#FFFFFF' },
  btnCancelText:      { color: '#5F584F' },
  btnDestructiveText: { color: '#FFFFFF' },
});

export default AppDialogHost;
